"use client";

import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { chairOf, health, issues, type Group, type Report } from "./data";
import { Dot, HealthBadge } from "./ui";

export function ReportBody({ report }: { report: Report }) {
    return (
        <div className="space-y-3 text-sm">
            {([["Updates", report.updates], ["Decisions needed", report.decisions], ["Risks", report.risks]] as const).map(([h, t]) => (
                <div key={h}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#007a8a]">{h}</div>
                    <div className="text-gray-700">{t}</div>
                </div>
            ))}
            <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#007a8a]">Projects</div>
                <ul className="mt-1 space-y-1">{report.projects.map((p) => <li key={p.name} className="flex items-center gap-2 text-gray-700"><Dot light={p.light} />{p.name}</li>)}</ul>
            </div>
            <p className="text-xs text-gray-400">Reports never include recordings or transcripts.</p>
        </div>
    );
}

/** One committee, as the Governance Committee or staff sees it. */
export function GroupDetail({ group, onClose, footer }: { group: Group | null; onClose: () => void; footer?: (g: Group) => React.ReactNode }) {
    return (
        <Dialog open={!!group} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                {group && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex flex-wrap items-center gap-2 text-[#003d45]">{group.name} <HealthBadge health={health(group)} /></DialogTitle>
                            <DialogDescription>{group.type} · Chair {chairOf(group)}{group.viceChair ? ` · Vice Chair ${group.viceChair}` : ""} · {group.members} members · staff liaison {group.liaison}</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-3 gap-2 text-center text-xs">
                            <div className="rounded-lg bg-gray-50 p-2"><div className="text-lg font-bold text-[#003d45]">{group.held} of {group.due}</div>meetings held</div>
                            <div className="rounded-lg bg-gray-50 p-2"><div className="text-lg font-bold text-[#003d45]">{group.planned}</div>planned this year</div>
                            <div className="rounded-lg bg-gray-50 p-2"><div className="text-lg font-bold text-[#003d45]">{group.held ? `${group.onTime} of ${group.held}` : "—"}</div>minutes on time</div>
                        </div>
                        <div className="space-y-1.5">
                            {issues(group).length === 0
                                ? <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="size-4" />Everything is on track.</p>
                                : issues(group).map((i) => <p key={i} className="flex items-center gap-2 text-sm text-amber-800"><AlertTriangle className="size-4 shrink-0" />{i}</p>)}
                        </div>
                        <div className="text-sm text-gray-600">
                            Last meeting {group.lastMeeting} · {group.lastStatus}. Next meeting {group.nextMeeting ?? "not scheduled"}. 2027: {group.nextYear.toLowerCase()}.
                        </div>
                        {group.covers && (
                            <div className="rounded-lg border border-gray-200 p-3">
                                <div className="mb-2 text-sm font-semibold text-gray-800">Chapters and communities report through this committee</div>
                                <ul className="space-y-1.5 text-sm">
                                    {group.covers.regions.map((r) => (
                                        <li key={r.name} className="flex flex-wrap justify-between gap-x-3 gap-y-0.5">
                                            <span className="text-gray-700">{r.name.replace(" Chapter Development Committee", "")}</span>
                                            <span className="text-xs text-gray-500">{r.chapters} chapters · {r.communities} communities · {r.chair ? `Chair ${r.chair}` : "no Chair yet"}</span>
                                        </li>
                                    ))}
                                </ul>
                                {group.covers.unassignedChapters > 0 && <p className="mt-2 text-xs text-amber-700">{group.covers.unassignedChapters} chapters are not yet under a regional committee.</p>}
                            </div>
                        )}
                        {group.policies.length > 0 && (
                            <details className="rounded-lg border border-gray-200 p-3">
                                <summary className="cursor-pointer text-sm font-semibold text-gray-800">Policies this committee owns ({group.policies.length})</summary>
                                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-gray-600">{group.policies.map((p) => <li key={p}>{p}</li>)}</ul>
                            </details>
                        )}
                        {group.report ? (
                            <div className="rounded-lg border border-gray-200 p-3">
                                <div className="mb-2 text-sm font-semibold text-gray-800">Latest report · meeting {group.report.meeting}</div>
                                <ReportBody report={group.report} />
                            </div>
                        ) : <p className="text-sm text-gray-400">No report yet this quarter.</p>}
                        {footer?.(group)}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
